import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable, DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Link, useNavigate } from 'react-router-dom';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { getPurchaseOrders, deletePurchaseOrder } from '../api';
import { PurchaseOrder, PurchaseOrderStatus } from '../types/purchaseOrder';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatCurrency } from '../utils';
import { UserRole } from '../types/user';
import axios from 'axios';

const PurchaseOrderList: React.FC = () => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: 'createdAt' as string,
    sortOrder: -1 as 1 | -1,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '' as PurchaseOrderStatus | '',
  });

  const { addNotification } = useNotification();
  const { user, token, refreshToken } = useAuth();
  const navigate = useNavigate();
  const dt = useRef<DataTable<PurchaseOrder[]>>(null);

  const loadPurchaseOrders = useCallback(async (retryWithRefresh = true) => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    try {
      setLoading(true);
      const [orders, total] = await getPurchaseOrders({
        page: lazyParams.page,
        limit: lazyParams.rows,
        sortBy: lazyParams.sortField,
        sortOrder: lazyParams.sortOrder === 1 ? 'asc' : 'desc',
        search: filters.search || undefined,
        status: filters.status || undefined,
      });

      setPurchaseOrders(orders);
      setTotalRecords(total);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      if (axios.isAxiosError(error) && error.response?.status === 401 && retryWithRefresh) {
        try {
          await refreshToken();
          // Retry the request after token refresh
          await loadPurchaseOrders(false);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          addNotification('error', 'Session expired. Please log in again.');
          navigate('/login');
        }
      } else {
        addNotification('error', 'Failed to fetch purchase orders');
      }
    } finally {
      setLoading(false);
    }
  }, [lazyParams, filters, addNotification, token, navigate, refreshToken]);

  useEffect(() => {
    if (token) {
      loadPurchaseOrders();
    } else {
      navigate('/login');
    }
  }, [loadPurchaseOrders, token, navigate]);

  const onPage = (event: DataTablePageEvent) => {
    setLazyParams(prevParams => ({
      ...prevParams,
      first: event.first,
      rows: event.rows,
      page: (event.first / event.rows) + 1,
    }));
  };

  const onSort = (event: DataTableSortEvent) => {
    setLazyParams(prevParams => ({
      ...prevParams,
      sortField: event.sortField,
      sortOrder: event.sortOrder === 0 ? -1 : event.sortOrder as 1 | -1,
    }));
  };

  const onFilter = (e: React.ChangeEvent<HTMLInputElement> | DropdownChangeEvent, field: string) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: 'value' in e ? e.value : e.target.value,
    }));
    setLazyParams(prevParams => ({
      ...prevParams,
      first: 0,
      page: 1,
    }));
  };

  const calculateTotalAmount = (po: PurchaseOrder) => {
    if (!po.items || po.items.length === 0) {
      return 0;
    }
    return po.items.reduce((total, item) => {
      const unitPrice = parseFloat(item.unitPrice.toString());
      const quantity = parseFloat(item.quantity.toString());
      return total + (unitPrice * quantity);
    }, 0);
  };

  const statusBodyTemplate = (rowData: PurchaseOrder) => {
    return (
      <span className="flex align-items-center gap-2">
        <span className={`customer-badge status-${rowData.status.toLowerCase()}`}>{rowData.status}</span>
        {rowData.needsReview && (
          <i
            className="pi pi-exclamation-triangle text-yellow-600"
            title={rowData.reviewReason || 'Needs review'}
          />
        )}
      </span>
    );
  };

  const handlingUnitsBodyTemplate = (rowData: PurchaseOrder) => {
    const allHUs = rowData.items?.flatMap(item => item.handlingUnits?.map(hu => hu.huNumber) || []) || [];
    return allHUs.join(', ');
  };

  const actionBodyTemplate = (rowData: PurchaseOrder) => {
    return (
      <div className="flex justify-content-center">
        <Link to={`/purchase-orders/${rowData.id}`}>
          <Button icon="pi pi-eye" className="p-button-rounded p-button-info mr-2" />
        </Link>
        {user?.role === UserRole.Admin && (
          <Button
            icon="pi pi-trash"
            className="p-button-rounded p-button-danger"
            onClick={() => confirmDelete(rowData)}
          />
        )}
      </div>
    );
  };

  const confirmDelete = (purchaseOrder: PurchaseOrder) => {
    confirmDialog({
      message: `Are you sure you want to delete the purchase order ${purchaseOrder.poNumber}?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => handleDelete(purchaseOrder.id!),
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePurchaseOrder(id);
      addNotification('success', 'Purchase order deleted successfully');
      loadPurchaseOrders();
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        try {
          await refreshToken();
          await deletePurchaseOrder(id);
          addNotification('success', 'Purchase order deleted successfully');
          loadPurchaseOrders();
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          addNotification('error', 'Session expired. Please log in again.');
          navigate('/login');
        }
      } else {
        addNotification('error', 'Failed to delete purchase order');
      }
    }
  };

  const renderSkeleton = () => {
    return (
      <div className="p-d-flex p-ai-center">
        <Skeleton width="100%" height="2rem" className="p-mb-2"></Skeleton>
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">Purchase Orders</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          type="search"
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => onFilter(e, 'search')}
          placeholder="Search PO or HU"
        />
      </span>
    </div>
  );

  if (!token) {
    return null;
  }

  return (
    <div className="card">
      <DataTable
        ref={dt}
        value={purchaseOrders}
        lazy
        paginator
        first={lazyParams.first}
        rows={lazyParams.rows}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        loading={loading}
        className="p-datatable-customers"
        dataKey="id"
        emptyMessage="No purchase orders found."
        header={header}
      >
        <Column
          field="poNumber"
          header="PO Number"
          sortable
        />
        <Column 
          field="client.name" 
          header="Client" 
          sortable 
          body={(rowData: PurchaseOrder) => rowData.client?.name || 'N/A'}
        />
        <Column
          field="status"
          header="Status"
          sortable
          body={statusBodyTemplate}
          filter
          filterElement={
            <Dropdown
              value={filters.status}
              options={Object.values(PurchaseOrderStatus)}
              onChange={(e) => onFilter(e, 'status')}
              placeholder="Select a Status"
              className="p-column-filter"
              showClear
            />
          }
        />
        <Column
          field="handlingUnits"
          header="Handling Units"
          body={handlingUnitsBodyTemplate}
        />
        <Column
          field="totalAmount"
          header="Total Amount"
          sortable
          body={(rowData: PurchaseOrder) => formatCurrency(calculateTotalAmount(rowData))}
        />
        <Column
          field="createdAt"
          header="Created At"
          sortable
          body={(rowData: PurchaseOrder) => rowData.createdAt ? formatDate(rowData.createdAt) : 'N/A'}
        />
        <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '8rem' }} />
      </DataTable>
      <ConfirmDialog />
    </div>
  );
};

export default PurchaseOrderList;
